/**
 * Servicio de gestión de suscripciones
 * Principios SOLID aplicados:
 * - SRP: Responsabilidad única de gestionar suscripciones de usuarios
 * - DIP: Usa ApiService en lugar de fetch directo
 */

import { adminApi } from './ApiService';

class SubscriptionService {
  /**
   * Actualiza el estado de suscripción de un usuario
   * @param {string} userId - ID del usuario
   * @param {boolean} subscriptionActive - true para activar, false para desactivar
   * @param {string} [plan] - Plan: 'free', 'basico', 'pro', 'comercial'
   * @param {boolean} [isTrial] - true/false para activar/desactivar trial
   * @returns {Promise<Object>} Respuesta de la API
   */
  async updateUserSubscription(userId, subscriptionActive, plan = null, isTrial = undefined) {
    try {
      const body = { subscriptionActive };
      if (plan) body.plan = plan;
      if (isTrial !== undefined) body.isTrial = isTrial;
      const data = await adminApi.patch(`/admin/subscriptions/${userId}`, body);
      return data;
    } catch (error) {
      console.error('Error al actualizar suscripción:', error);
      throw error;
    }
  }

  /**
   * Cambia el plan de un usuario
   * @param {string} userId - ID del usuario
   * @param {string} plan - Plan: 'free', 'basico', 'pro', 'comercial'
   * @returns {Promise<Object>} Respuesta de la API
   */
  async changePlan(userId, plan) {
    const isActive = plan !== 'free';
    return this.updateUserSubscription(userId, isActive, plan);
  }

  /**
   * Activa la suscripción de un usuario (quita trial si lo tenía)
   * @param {string} userId - ID del usuario
   * @returns {Promise<Object>} Respuesta de la API
   */
  async activateSubscription(userId) {
    return this.updateUserSubscription(userId, true, null, false);
  }

  /**
   * Desactiva la suscripción de un usuario
   * @param {string} userId - ID del usuario
   * @returns {Promise<Object>} Respuesta de la API
   */
  async deactivateSubscription(userId) {
    return this.updateUserSubscription(userId, false, null, false);
  }

  /**
   * Activa/desactiva el período de prueba de un usuario
   * @param {string} userId - ID del usuario
   * @param {boolean} enable - true para activar trial, false para quitar
   * @param {string} [plan] - Plan del trial (default: pro)
   * @returns {Promise<Object>} Respuesta de la API
   */
  async toggleTrial(userId, enable, plan = 'pro') {
    const body = { subscriptionActive: enable, isTrial: enable };
    if (enable) {
      body.plan = plan;
      const trialEnds = new Date();
      trialEnds.setDate(trialEnds.getDate() + 7);
      body.subscriptionExpiresAt = trialEnds.toISOString();
    }
    return adminApi.patch(`/admin/subscriptions/${userId}`, body);
  }

  /**
   * Activa/desactiva whitelist de un usuario (suscripción gratis sin expiración)
   * @param {string} userId - ID del usuario
   * @param {boolean} enable - true para whitelistear, false para quitar
   * @param {string} [plan] - Plan a asignar (default: pro)
   * @returns {Promise<Object>} Respuesta de la API
   */
  async toggleWhitelist(userId, enable, plan = 'pro') {
    const body = { isWhitelisted: enable };
    if (enable) {
      body.subscriptionActive = true;
      body.plan = plan;
      body.isTrial = false;
    }
    return adminApi.patch(`/admin/subscriptions/${userId}`, body);
  }

  /**
   * Registra cuota manual (pago por transferencia bancaria)
   * @param {string} userId - ID del usuario
   * @param {string} plan - Plan: 'basico', 'pro', 'comercial'
   * @param {string} billingCycle - 'monthly' (1 mes) o 'annual' (1 año)
   * @returns {Promise<Object>} Respuesta de la API
   */
  async registerManualPayment(userId, plan, billingCycle) {
    return adminApi.patch(`/admin/subscriptions/${userId}`, {
      manualPayment: true,
      plan,
      billingCycle,
    });
  }

  /**
   * Obtiene historial de pagos de un cliente (MP authorized_payments)
   * @param {string} userId - ID del usuario
   * @returns {Promise<Object>} { payments, preapprovalId, plan, manualPayment, mercadopagoStatus }
   */
  async getPaymentHistory(userId) {
    return adminApi.get(`/admin/clients/${userId}/payments`);
  }

  /**
   * Obtiene estadísticas de suscripciones
   * @returns {Promise<Object>} Estadísticas del caché de suscripciones
   */
  async getSubscriptionStats() {
    try {
      const response = await adminApi.get('/admin/subscriptions/stats');
      return response;
    } catch (error) {
      console.error('Error al obtener estadísticas de suscripciones:', error);
      throw error;
    }
  }
}

// Exportar instancia única (Singleton)
export default new SubscriptionService();
